import { useState } from 'react';
import { getAuth } from 'firebase/auth';
import clsx from 'clsx';
import { CheckCircleIcon, ExclamationCircleIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { publicacaoService } from '../publicacaoService';
import type { AnexoPublicacao } from '../publicacao.types';

export const TAMANHO_MAXIMO_MB = 15;
export const TAMANHO_MAXIMO_ARQUIVO = TAMANHO_MAXIMO_MB * 1024 * 1024;

const TIPOS_ACEITOS_TEXTO: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/webp': 'WEBP',
  'text/html': 'HTML',
};

const megas = (bytes: number) => (bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/** Mensagem de erro pra quem publica — explica o que houve e o que fazer, sem pedir pra sair do sistema. */
function mensagemDeErro(codigo: string): string {
  if (codigo.includes('unauthorized')) {
    return 'Você não tem permissão para anexar arquivos aqui. Peça a permissão "Publicar no Portal" a um administrador do Digital.';
  }
  if (codigo.includes('quota')) return 'O espaço de arquivos do Digital está cheio. Avise o time responsável pelo sistema.';
  if (codigo.includes('retry-limit') || codigo.includes('network') || codigo.includes('unknown')) {
    return 'Não foi possível anexar agora. Verifique sua conexão com a internet e tente de novo.';
  }
  return 'Não foi possível anexar o arquivo. Tente de novo em instantes.';
}

interface ChipArquivoProps {
  anexo: AnexoPublicacao;
  /** Enviado agora há pouco — mostra a animação de "enviado". */
  recemEnviado?: boolean;
  onRemover: () => void;
}

/** Arquivo já enviado: ✓ verde (animado quando acabou de subir), nome com link e X pra remover. */
export function ChipArquivo({ anexo, recemEnviado = false, onRemover }: ChipArquivoProps) {
  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
        recemEnviado ? 'border-green-200 bg-green-50' : 'border-raro-gray-200 bg-white'
      )}
    >
      <CheckCircleIcon
        className={clsx('h-5 w-5 shrink-0 text-green-600', recemEnviado && 'animate-[bounce_0.6s_ease-out_1]')}
        aria-hidden="true"
      />
      <a href={anexo.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-raro-gray-800 hover:text-primary-700">
        {anexo.nome}
      </a>
      {recemEnviado && <span className="shrink-0 text-xs font-semibold text-green-700">Enviado</span>}
      <button
        type="button"
        onClick={onRemover}
        className="shrink-0 rounded-full p-1 text-raro-gray-400 hover:bg-rose-50 hover:text-rose-600"
        aria-label={`Remover ${anexo.nome}`}
        title="Remover"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

interface EnvioArquivoProps {
  publicacaoId: string;
  pasta: 'capa' | 'anexos' | 'documento';
  accept: string;
  texto: string;
  /** Chamado quando o arquivo terminou de subir. */
  onEnviado: (anexo: AnexoPublicacao) => void;
  multiplo?: boolean;
}

interface EmEnvio {
  nome: string;
  progresso: number;
  erro: string | null;
}

/**
 * Área de envio: o arquivo sobe NA HORA em que é escolhido (barra de progresso), pra quem publica
 * saber que deu certo — antes só subia ao salvar e não dava pra ver se tinha ido. Erros (tamanho,
 * permissão) aparecem na própria área.
 */
export function EnvioArquivo({ publicacaoId, pasta, accept, texto, onEnviado, multiplo = false }: EnvioArquivoProps) {
  const [envios, setEnvios] = useState<EmEnvio[]>([]);

  const enviar = async (arquivo: File) => {
    const atualizar = (parcial: Partial<EmEnvio>) =>
      setEnvios((atual) => atual.map((e) => (e.nome === arquivo.name ? { ...e, ...parcial } : e)));
    setEnvios((atual) => [...atual.filter((e) => e.nome !== arquivo.name), { nome: arquivo.name, progresso: 0, erro: null }]);
    if (arquivo.size > TAMANHO_MAXIMO_ARQUIVO) {
      atualizar({
        erro: `Este arquivo tem ${megas(arquivo.size)} MB e não foi possível anexar. Escolha um arquivo com menos de ${TAMANHO_MAXIMO_MB} MB.`,
      });
      return;
    }
    const aceitos = accept.split(',').map((t) => t.trim());
    if (arquivo.type && !aceitos.includes(arquivo.type)) {
      const formatos = aceitos.map((t) => TIPOS_ACEITOS_TEXTO[t] ?? t).join(', ');
      atualizar({ erro: `Esse tipo de arquivo não é aceito aqui. Envie um arquivo ${formatos}.` });
      return;
    }
    const tentar = () => publicacaoService.enviarArquivo(publicacaoId, arquivo, pasta, (progresso) => atualizar({ progresso }));
    try {
      let anexo;
      try {
        anexo = await tentar();
      } catch (error) {
        // Permissão recém-dada ainda não chegou ao login: renova a sessão sozinho e tenta de novo,
        // sem a pessoa precisar sair e entrar (e perder o que já preencheu).
        if (!((error as { code?: string })?.code ?? '').includes('unauthorized')) throw error;
        await getAuth().currentUser?.getIdToken(true);
        atualizar({ progresso: 0 });
        anexo = await tentar();
      }
      setEnvios((atual) => atual.filter((e) => e.nome !== arquivo.name));
      onEnviado(anexo);
    } catch (error) {
      atualizar({ erro: mensagemDeErro((error as { code?: string })?.code ?? '') });
    }
  };

  return (
    <div className="space-y-2">
      {envios.map((envio) => (
        <div key={envio.nome} className="rounded-lg border border-raro-gray-200 bg-white px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            {envio.erro ? (
              <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" />
            ) : (
              <PaperClipIcon className="h-5 w-5 shrink-0 animate-pulse text-primary-500" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate">{envio.nome}</span>
            {envio.erro ? (
              <button
                type="button"
                className="shrink-0 rounded-full p-1 text-raro-gray-400 hover:text-raro-gray-600"
                aria-label="Fechar aviso"
                onClick={() => setEnvios((atual) => atual.filter((e) => e.nome !== envio.nome))}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : (
              <span className="shrink-0 text-xs text-raro-gray-500">Enviando... {envio.progresso}%</span>
            )}
          </div>
          {envio.erro ? (
            <p className="mt-1 text-xs text-rose-700">{envio.erro}</p>
          ) : (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-raro-gray-100">
              <div className="h-1.5 rounded-full bg-primary-600 transition-all duration-200" style={{ width: `${envio.progresso}%` }} />
            </div>
          )}
        </div>
      ))}
      <label className="flex cursor-pointer justify-center rounded-lg border border-dashed border-raro-gray-300 px-3 py-3 text-sm text-raro-gray-500 transition-colors hover:border-primary-300 hover:bg-primary-50/40">
        {texto}
        <input
          type="file"
          multiple={multiplo}
          accept={accept}
          className="sr-only"
          onChange={(event) => {
            Array.from(event.target.files ?? []).forEach((arquivo) => void enviar(arquivo));
            event.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
